import { useRef, useEffect, useCallback } from 'react';

// Diagnostic instance counter — a mismatch between the instance that scheduled a
// fire and the instance active when it fires would indicate the hook itself was
// torn down/recreated (e.g. an unexpected remount) while an old timer kept ticking.
let _instanceSeq = 0;

const useWatchdog = (callback, delay) => {
  const savedCallback = useRef();
  const timerId = useRef(null);
  const activeRef = useRef(false);       // is watchdog running?
  const deadlineRef = useRef(null);      // absolute time the timer should fire
  const instanceId = useRef(++_instanceSeq).current;
  const startCount = useRef(0);
  // Bumped every time a new timer is scheduled (by start() OR the visibility
  // reschedule). A fired timer checks its own captured generation against this —
  // if a newer timer superseded it, generationRef has moved on and it's a no-op.
  // This is a second line of defense: clear() below should already have cancelled
  // any predecessor via clearTimeout, but a guard makes an orphaned timer harmless
  // even if some future code path schedules a setTimeout without going through
  // clear() first (exactly the bug this replaces — the visibility-reschedule
  // branch used to call setTimeout directly without clearing timerId.current,
  // orphaning whatever timer was previously scheduled so no later start() could
  // reach it to cancel it).
  const generationRef = useRef(0);

  // Save the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Cancel whatever timer is currently scheduled and null out the ref so it can
  // never be mistaken for "the" live timer again. ALL scheduling paths (start(),
  // stop(), and the visibility-change reschedule) must call this before touching
  // timerId.current — that invariant is what makes a single ref safe to rely on.
  const clear = useCallback(() => {
    if (timerId.current !== null) {
      clearTimeout(timerId.current);
      timerId.current = null;
    }
  }, []);

  // Schedule a fire in `ms` milliseconds, tagged with a fresh generation. Shared by
  // start() and the visibility-change reschedule so both paths are guaranteed to
  // clear before scheduling and to check the generation guard before firing.
  const schedule = useCallback((ms, label) => {
    clear();
    const scheduledAt = Date.now();
    const myGen = ++generationRef.current;
    const seq = ++startCount.current;
    console.debug(
      `[useWatchdog] ${label} #${seq} instance=${instanceId} gen=${myGen} scheduledAt=${scheduledAt} fireInMs=${ms}`
    );
    timerId.current = setTimeout(() => {
      if (generationRef.current !== myGen) {
        // Superseded by a later start()/reschedule that (correctly) called clear()
        // first — this callback firing at all means clearTimeout on some earlier
        // path didn't actually cancel it. Should be unreachable; log if it happens.
        console.warn(
          `[useWatchdog] stale timer fired but was superseded — ignoring. ` +
          `#${seq} instance=${instanceId} gen=${myGen} currentGen=${generationRef.current}`
        );
        return;
      }
      console.debug(
        `[useWatchdog] firing timer scheduled by ${label} #${seq} instance=${instanceId} ` +
        `gen=${myGen} scheduledAt=${scheduledAt} now=${Date.now()}`
      );
      activeRef.current = false;
      timerId.current = null;
      if (savedCallback.current) savedCallback.current();
    }, ms);
  }, [clear, instanceId]);

  // Start (or restart) the timer. If the tab is currently hidden, only record
  // the intent to run (activeRef/deadlineRef) — do not arm a live setTimeout.
  // The visibilitychange handler already clears any timer on hide and is the
  // sole path that (re)schedules while hidden->visible; a reset arriving while
  // already hidden (e.g. a backend SSE ping — hidden tabs still receive SSE
  // right up until a full Page Lifecycle "freeze", unlike a suspended timer)
  // must not re-arm a timer that then runs to completion in the background,
  // which would defeat the suspend-while-hidden protection entirely (WEBAPP-3S
  // recurrence, PLEX-3931).
  const start = useCallback(() => {
    activeRef.current = true;
    deadlineRef.current = Date.now() + delay;
    if (typeof document !== "undefined" && document.hidden) {
      clear();
      return;
    }
    schedule(delay, 'start()');
  }, [delay, schedule, clear]);

  // Stop the timer
  const stop = useCallback(() => {
    console.debug(`[useWatchdog] stop() instance=${instanceId} lastStartCount=${startCount.current}`);
    activeRef.current = false;
    deadlineRef.current = null;
    generationRef.current++; // invalidate any in-flight timer without needing it to fire
    clear();
  }, [clear, instanceId]);

  // Suspend/resume timer on tab visibility changes to prevent spurious fires
  // when Chrome throttles background-tab timers.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        // Tab going to background — suspend the timer, preserve remaining time.
        // Do NOT bump the generation here: deadlineRef is still valid and the
        // foreground-reschedule below needs schedule() to establish a fresh one.
        console.debug(`[useWatchdog] tab hidden instance=${instanceId} active=${activeRef.current} deadline=${deadlineRef.current}`);
        clear();
      } else if (activeRef.current && deadlineRef.current !== null) {
        // Tab back in foreground — reschedule with remaining time.
        const now = Date.now();
        const remaining = deadlineRef.current - now;
        // If the whole delay window already elapsed while hidden, wall-clock time is
        // not reliable evidence of a stalled connection: browsers can fully freeze a
        // backgrounded tab's JS execution (Page Lifecycle API "frozen" state), which
        // suspends delivery of already-buffered SSE/poll events to this code exactly
        // as much as it suspends this timer. Firing immediately on unfreeze reports a
        // false-positive timeout for a stream that was healthy the entire time but
        // simply couldn't be observed while the tab was frozen (WEBAPP-3S). Give the
        // connection a full fresh window to prove itself instead of condemning it on
        // a stale deadline.
        const rescheduleMs = remaining > 0 ? remaining : delay;
        if (remaining <= 0) {
          deadlineRef.current = now + delay;
        }
        console.debug(`[useWatchdog] tab visible instance=${instanceId} remaining=${remaining} rescheduleMs=${rescheduleMs}`);
        schedule(rescheduleMs, 'visibility-reschedule');
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [schedule, clear, instanceId, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      generationRef.current++;
      clear();
    };
  }, [clear]);

  return [start, stop];
}

export default useWatchdog;
